/** @file Emits a circle outline (16-segment quad strip) of a given pixel radius and stroke width. */
import type { Sink } from './createSink';
import { emitMesh, type PixelMesh } from './emitMesh';
import { unitCircle } from './unitCircle';

const SEGMENTS = 16;
const xy = new Float64Array(4 * SEGMENTS);
/** Per segment i: outer 2i, inner 2i + 1, next outer/inner wrap around. */
const STRIP = Array.from({ length: SEGMENTS }, (_, i) => {
  const o0 = 2 * i, i0 = o0 + 1, o1 = (2 * (i + 1)) % (2 * SEGMENTS), i1 = o1 + 1;
  return [o0, o1, i1, o0, i1, i0];
}).flat();
/** cos / sin of the segment angles, through trig once (the same values every call computed). */
const [COS, SIN] = unitCircle(SEGMENTS);
/** The primitive, refilled per call (no object per ring). */
const mesh: PixelMesh = { page: 0, count: 2 * SEGMENTS, xy, uv: null, light: 0, lights: null, indices: STRIP };

/** Centre (cx, cy), radius (centre of the stroke) and stroke width in pixels. */
export function emitRing(sink: Sink, cx: number, cy: number, radius: number, width: number, light: number): void {
  const outer = radius + width / 2, inner = Math.max(0, radius - width / 2);
  for (let i = 0; i < SEGMENTS; i++) {
    const c = COS[i] ?? 0, s = SIN[i] ?? 0;
    xy[4 * i] = cx + outer * c;
    xy[4 * i + 1] = cy + outer * s;
    xy[4 * i + 2] = cx + inner * c;
    xy[4 * i + 3] = cy + inner * s;
  }
  mesh.page = sink.white;
  mesh.light = light;
  emitMesh(sink, mesh);
}
